import React, { useState, useEffect } from "react";
import { generateFireRiskPdf, generateFireRiskDocx } from "./utilis/generateReport";
import "./AuditForm.css";
import DocumentHeaderForm from "../components/dataenty/DocumentHeaderForm";
import ConsultancyIntroForm from "../components/dataenty/ConsultancyIntroForm";
import LegislationForm from "../components/dataenty/LegislationForm";
import ExecutiveSummaryForm from "../components/dataenty/ExecutiveSummaryForm";
import OccupantsForm from "../components/dataenty/OccupantsForm";
import FireLossForm from "../components/dataenty/FireLossForm";
import ChecklistSection from "../components/dataenty/ChecklistSection";  // checklist section
import ManagementRecordsForm from "../components/dataenty/ManagementRecordsForm";
import ActionPlanForm from "../components/dataenty/ActionPlanForm";
import ReviewForm from "../components/dataenty/ReviewForm";
import SignatureForm from "../components/dataenty/SignatureForm";
import PhotoEvidenceSection from "../components/dataenty/PhotoEvidenceSection";
import {
  fireHazardsQuestions,
  fireProtectionQuestions,
  managementQuestions,
} from "./checklistQuestions";  //  we need more questions than the ones which are in there 

const steps = [
  "Header", "Consultancy Intro", "Legislation", "Executive Summary",
  "Occupants & Persons at Risk", "Fire Loss Experience",
  "Section 7: Fire Hazards", "Section 8: Fire Protection",
  "Section 9: Fire Safety Management", "Records/Drills", "Action Plan", "Review", "Signatures", "Photo Evidence"
];

const stepHelpText = [
  "Enter the document header and core details for this fire risk assessment.",
  "Add the consultancy introduction, covering letter, and responsible persons.",
  "Add ALL relevant legislation and standards (PAS 79, RRFSO, BS, etc).",
  "Summarise the key findings, scope, strategy, concerns, and conclusion.",
  "Detail all types and counts of occupants and persons at special risk.",
  "Log any fire loss experience, nature, and consequences.",
  "For each PAS 79 hazard clause, answer Yes/No/N/A, record deficiencies and evidence.",
  "For each protection clause, answer Yes/No/N/A, record deficiencies and evidence.",
  "For each management question, answer Yes/No/N/A, record deficiencies and evidence.",
  "Describe fire logbook, alarm tests, drills, and management records.",
  "Log all action plan issues, priorities, timescales, and responsibilities.",
  "Complete follow-up/review with date, reason, and outstanding actions.",
  "Add all signatures for validation (Assessor, Validator, Client).",
  "Attach all supporting evidence photos or documentation."
];

const makeChecklistInit = arr =>
  Object.fromEntries((arr || []).map(q => [q.key, { answer: "", deficiencies: "", evidence: null }]));

const defaultState = {
  header: {
    auditNumber: "", auditTitle: "", dateConducted: "", assessor: "",
    reviewer: "", client: "", siteAddress: ""
  },
  consultancyIntro: {
    introLetter: "",
    company: "",
    consultantContact: "",
    reviewerContact: ""
  },
  legislation: [""],
  executiveSummary: {
    purpose: "", scope: "", strategy: "", fireHistory: "",
    lifeSafety: "", property: "", business: "", environmental: "", conclusion: ""
  },
  occupants: {
    general: "", sleeping: "", disabled: "", young: "", visitors: "", other: ""
  },
  fireLosses: [{ date: "", nature: "", consequences: "" }],
  fireHazards: makeChecklistInit(fireHazardsQuestions),
  fireProtection: makeChecklistInit(fireProtectionQuestions),
  management: makeChecklistInit(managementQuestions),
  managementRecords: { logbook: "", alarmTests: "", drills: "" },
  actionPlan: [],
  review: { date: "", reason: "", followUp: "" },
  signatures: {},
  evidenceFiles: []
};

const LOCAL_STORAGE_KEY = "fra-fireRiskAssessmentForm-pro";

const FireRiskAssessmentForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      return saved ? { ...defaultState, ...JSON.parse(saved) } : { ...defaultState };
    } catch {
      return { ...defaultState };
    }
  });
  const [imageFiles, setImageFiles] = useState({}); // Separate state for File objects

  // Autosave to localStorage, excluding File objects
  useEffect(() => {
    const saveData = {
      ...formData,
      evidenceFiles: formData.evidenceFiles.map(({ url, name }) => ({ url, name })), // Exclude File
      fireHazards: Object.fromEntries(
        Object.entries(formData.fireHazards).map(([key, val]) => [
          key,
          { ...val, evidence: val.evidence ? { url: val.evidence.url, name: val.evidence.name } : null }
        ])
      ),
      fireProtection: Object.fromEntries(
        Object.entries(formData.fireProtection).map(([key, val]) => [
          key,
          { ...val, evidence: val.evidence ? { url: val.evidence.url, name: val.evidence.name } : null }
        ])
      ),
      management: Object.fromEntries(
        Object.entries(formData.management).map(([key, val]) => [
          key,
          { ...val, evidence: val.evidence ? { url: val.evidence.url, name: val.evidence.name } : null }
        ])
      )
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(saveData));
  }, [formData]);

  // Reattach File objects when loading from localStorage
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      evidenceFiles: prev.evidenceFiles.map(item => ({
        ...item,
        file: imageFiles[item.url] || null
      })),
      fireHazards: Object.fromEntries(
        Object.entries(prev.fireHazards).map(([key, val]) => [
          key,
          { ...val, evidence: val.evidence ? { ...val.evidence, file: imageFiles[val.evidence.url] || null } : null }
        ])
      ),
      fireProtection: Object.fromEntries(
        Object.entries(prev.fireProtection).map(([key, val]) => [
          key,
          { ...val, evidence: val.evidence ? { ...val.evidence, file: imageFiles[val.evidence.url] || null } : null }
        ])
      ),
      management: Object.fromEntries(
        Object.entries(prev.management).map(([key, val]) => [
          key,
          { ...val, evidence: val.evidence ? { ...val.evidence, file: imageFiles[val.evidence.url] || null } : null }
        ])
      )
    }));
  }, [imageFiles]);

  // Update imageFiles when evidence changes
  const updateImageFiles = (newEvidence, key, section) => {
    setImageFiles(prev => {
      const newFiles = { ...prev };
      if (section) {
        // ChecklistSection evidence
        if (newEvidence) {
          newFiles[newEvidence.url] = newEvidence.file;
        } else {
          // Remove file
          const oldUrl = formData[section][key]?.evidence?.url;
          if (oldUrl) delete newFiles[oldUrl];
        }
      } else {
        // PhotoEvidenceSection evidence
        newEvidence.forEach(item => {
          if (item.url) newFiles[item.url] = item.file;
        });
      }
      return newFiles;
    });
  };

  // Modular Section Handlers
  const setHeader = val => setFormData(f => ({ ...f, header: val }));
  const setConsultancyIntro = val => setFormData(f => ({ ...f, consultancyIntro: val }));
  const setLegislation = arr => setFormData(f => ({ ...f, legislation: arr }));
  const setExecutiveSummary = val => setFormData(f => ({ ...f, executiveSummary: val }));
  const setOccupants = val => setFormData(f => ({ ...f, occupants: val }));
  const setFireLosses = arr => setFormData(f => ({ ...f, fireLosses: arr }));
  const setFireHazards = obj => {
    setFormData(f => ({ ...f, fireHazards: obj }));
    Object.entries(obj).forEach(([key, val]) => updateImageFiles(val.evidence, key, "fireHazards"));
  };
  const setFireProtection = obj => {
    setFormData(f => ({ ...f, fireProtection: obj }));
    Object.entries(obj).forEach(([key, val]) => updateImageFiles(val.evidence, key, "fireProtection"));
  };
  const setManagement = obj => {
    setFormData(f => ({ ...f, management: obj }));
    Object.entries(obj).forEach(([key, val]) => updateImageFiles(val.evidence, key, "management"));
  };
  const setManagementRecords = val => setFormData(f => ({ ...f, managementRecords: val }));
  const setActionPlan = arr => setFormData(f => ({ ...f, actionPlan: arr }));
  const setReview = val => setFormData(f => ({ ...f, review: { ...f.review, ...val } }));
  const setSignatures = val => setFormData(f => ({ ...f, signatures: val }));
  const setEvidenceFiles = arr => {
    setFormData(f => ({ ...f, evidenceFiles: arr }));
    updateImageFiles(arr);
  };

  const handleDownloadPdf = async () => {
    console.log("formData:", JSON.stringify(formData, (k, v) => (v instanceof File ? "File" : v), 2));
    try {
      await generateFireRiskPdf(formData, { fireHazardsQuestions, fireProtectionQuestions, managementQuestions });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please check the console for details.");
    }
  };

  const handleDownloadDocx = async () => {
    console.log("formData:", JSON.stringify(formData, (k, v) => (v instanceof File ? "File" : v), 2));
    try {
      await generateFireRiskDocx(formData, { fireHazardsQuestions, fireProtectionQuestions, managementQuestions });
    } catch (error) {
      console.error("Error generating DOCX:", error);
      alert("Failed to generate DOCX. Please check the console for details.");
    }
  };

  return (
    <div className="fra-wrapper">
      <div className="fra-progress-container">
        <div className="fra-progress-track">
          <div
            className="fra-progress-fill"
            style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
          />
        </div>
        <div className="fra-progress-text">
          <strong>Step {step}</strong> of {steps.length}
        </div>
      </div>

      <h2 className="fra-section-title">{steps[step - 1]}</h2>
      <div className="fra-step-help" style={{ color: "#236b50", marginBottom: "1rem", fontSize: "1.1rem" }}>
        {stepHelpText[step - 1]}
      </div>

      {step === 1 && <DocumentHeaderForm data={formData.header} onChange={setHeader} />}
      {step === 2 && <ConsultancyIntroForm data={formData.consultancyIntro} onChange={setConsultancyIntro} />}
      {step === 3 && <LegislationForm legislation={formData.legislation} setLegislation={setLegislation} />}
      {step === 4 && <ExecutiveSummaryForm data={formData.executiveSummary} onChange={setExecutiveSummary} />}
      {step === 5 && <OccupantsForm data={formData.occupants} onChange={setOccupants} />}
      {step === 6 && <FireLossForm fireLosses={formData.fireLosses} setFireLosses={setFireLosses} />}
      {step === 7 && (
        <ChecklistSection
          sectionTitle="Section 7: Fire Hazards"
          questions={fireHazardsQuestions}
          data={formData.fireHazards}
          setData={setFireHazards}
        />
      )}
      {step === 8 && (
        <ChecklistSection
          sectionTitle="Section 8: Fire Protection"
          questions={fireProtectionQuestions}
          data={formData.fireProtection}
          setData={setFireProtection}
        />
      )}
      {step === 9 && (
        <ChecklistSection
          sectionTitle="Section 9: Fire Safety Management"
          questions={managementQuestions}
          data={formData.management}
          setData={setManagement}
        />
      )}
      {step === 10 && <ManagementRecordsForm records={formData.managementRecords} setRecords={setManagementRecords} />}
      {step === 11 && <ActionPlanForm actionPlan={formData.actionPlan} setActionPlan={setActionPlan} />}
      {step === 12 && <ReviewForm formData={formData.review || {}} setFormData={setReview} />}
      {step === 13 && <SignatureForm signatures={formData.signatures} setSignatures={setSignatures} />}
      {step === 14 && <PhotoEvidenceSection evidenceFiles={formData.evidenceFiles} setEvidenceFiles={setEvidenceFiles} />}

      <div className="fra-form-nav-buttons">
        <button onClick={() => setStep(s => s - 1)} disabled={step === 1}>Back</button>
        <button onClick={() => setStep(s => s + 1)} disabled={step === steps.length}>Next</button>
        {step === steps.length && (
          <>
            <button onClick={handleDownloadPdf}>Download PDF</button>
            <button onClick={handleDownloadDocx}>Download DOCX</button>
          </>
        )}
      </div>
    </div>
  );
};

export default FireRiskAssessmentForm;